import {
  Stepper as MuiStepper,
  Step as MuiStep,
  StepLabel as MuiStepLabel,
  StepContent as MuiStepContent,
  Typography,
  Box
} from '@mui/material';
import { FC } from 'react';
import { StepIcon } from './stepIcon';
import CheckIcon from '../../../../icons/svg/checkmark.svg?react';

export interface StepElement {
  title: string;
  subTitle?: string;
  description?: string;
  icon: React.ElementType;
}

interface Props {
  config: Array<StepElement>;
  activeStep: number;
  editMode?: boolean;
}

export const VerticalStepper: FC<Props> = ({ config, activeStep, editMode }) => {
  return (
    <MuiStepper
      orientation="vertical"
      activeStep={activeStep}
      sx={(theme) => ({
        '& .MuiStepConnector-root': {
          marginLeft: '14px',
          '.MuiStepConnector-line': {
            borderLeftWidth: '3px'
          }
        },
        '& .MuiStepContent-root': {
          marginLeft: '14px',
          paddingLeft: '30px',
          borderLeftWidth: '3px'
        },
        '& .MuiStepConnector-line, .MuiStepContent-root': {
          borderColor: theme.palette.grey['100']
        },
        '& .Mui-active, .Mui-completed': {
          '& .MuiStepConnector-line': {
            borderColor: theme.palette.success.main
          }
        },
        ...(editMode && {
          '& .MuiStepConnector-line': {
            borderColor: `${theme.palette.success.main} !important`
          }
        }),
        '& .MuiStepIcon-root': {
          width: '32px',
          height: '32px'
        },
        '& .MuiStepLabel-iconContainer': {
          paddingRight: '14px'
        },
        '& .MuiStep-root:last-child': {
          '& .MuiStepContent-root': {
            marginLeft: '16px'
          }
        },
        '& .MuiStep-root': {
          '& .MuiTypography-root': {
            color: 'white'
          },
          ...(editMode
            ? {
                '& .Mui-disabled .MuiTypography-root': {
                  color: theme.palette.primary.main
                }
              }
            : {
                '& .Mui-disabled .MuiTypography-root': {
                  color: theme.palette.grey['100']
                }
              }),
          '& .MuiCollapse-root': {
            '& .MuiTypography-root': {
              color: theme.palette.primary.main
            }
          },
          '& .Mui-active, .Mui-completed': {
            '& .MuiTypography-root': {
              color: theme.palette.primary.main,
              '&.subtitle': {
                color: theme.palette.grey['500']
              }
            }
          }
        }
      })}
    >
      {config.map((step, index) => {
        const isPastStep = activeStep > index;
        const isActive = index === activeStep;
        return (
          <MuiStep key={index + step.title} completed={(editMode && !isActive) || isPastStep}>
            <MuiStepLabel
              StepIconComponent={StepIcon(
                isPastStep || (editMode && !isActive) ? CheckIcon : step.icon
              )}
              sx={(theme) => ({
                color: theme.palette.grey['100'],

                '& .Mui-completed': {
                  color: `${theme.palette.primary.main}!important`
                },
                '& .Mui-active': {
                  color: `${theme.palette.primary.main}!important`
                },
                ...(editMode &&
                  !isActive && {
                    '& .MuiStepIcon-root': {
                      color: `${theme.palette.success.main}!important`
                    }
                  })
              })}
            >
              <Box>
                <Typography
                  className="subtitle"
                  color="inherit!important"
                  sx={{
                    lineHeight: '1rem',
                    fontSize: '0.625rem'
                  }}
                >
                  {step.subTitle ?? `STEP ${String(index + 1).padStart(2, '0')}`}
                </Typography>
              </Box>
              <Typography
                sx={{
                  lineHeight: '1rem',
                  fontSize: '1rem',
                  color: 'inherit!important'
                }}
              >
                {step.title}
              </Typography>
            </MuiStepLabel>
            <MuiStepContent>
              <Typography sx={{ fontSize: '0.75rem', lineHeight: '1rem' }}>
                {step.description}
              </Typography>
            </MuiStepContent>
          </MuiStep>
        );
      })}
    </MuiStepper>
  );
};
